import { spaceFromPath, type PanelSpace } from '@/stores/space.store';

/**
 * Modules du panel : une fonctionnalité de Supervision, de Configuration ou de Statistiques qu'un
 * admin active ou coupe, globalement et projet par projet (Paramètres généraux > Fonctionnalités).
 *
 * Ce fichier ne dit QUE quelles URL appartiennent à quel module : c'est de la structure de
 * navigation, identique pour tous les projets. Les libellés, descriptions, espaces, parents et
 * projets actifs par défaut viennent du serveur (GET /api/panel/features), et l'activation par
 * projet de GET /api/panel/project-pages : rien de propre à un projet n'est codé ici.
 *
 * Miroir des clés de PanelFeatures.ALL côté API.
 */
export type PanelModuleKey =
  | 'logs' | 'console' | 'sanctions' | 'banwords' | 'whitelist' | 'staff' | 'players' | 'grades'
  | 'blocks' | 'items' | 'entities' | 'crafts' | 'lootbox' | 'jobs' | 'recubejobs' | 'weapons' | 'minerais' | 'adminshop' | 'factions'
  | 'worlds' | 'cosmetics' | 'requests' | 'discord'
  | 'stats' | 'statsFactions' | 'statsGrades' | 'statsAdminShop' | 'statsGuilds' | 'statsBox' | 'statsPersonas' | 'statsShop';

interface ModuleRoute {
  /** Racine d'URL du module : elle-même et tout ce qui est en dessous lui appartiennent. */
  path: string;
  module: PanelModuleKey;
  /** Route réservée aux admins (AdminRoute) : ne compte pas pour un autre membre du staff. */
  adminOnly?: boolean;
  /**
   * Clés de page du grade dont une suffit pour ouvrir l'écran (miroir de la barre latérale). Sans
   * elles, la route compte pour tout le monde. Sert à la cible d'un onglet d'espace : il ne mène pas
   * à un module que le grade ne peut pas ouvrir.
   */
  pages?: string[];
  /** Droits qui ouvrent aussi l'écran tant qu'un autre module est coupé (cf PagesWhenOff). */
  pagesWhenOff?: PagesWhenOff;
}

/**
 * Repli d'un écran : des droits qui l'ouvrent AUSSI tant qu'un autre module est coupé pour le
 * projet, parce que l'écran reprend alors des réglages dont la place habituelle est fermée.
 */
export interface PagesWhenOff {
  module: PanelModuleKey;
  pages: string[];
}

/**
 * Réglage de chaque grade (ornement, rank tag, chat, boutique), sous le droit config.grades : sa
 * place est le détail d'un grade de Supervision > Grades (module « grades »). Ce module coupé pour
 * le projet, Configuration > Cosmétiques des grades le reprend, à côté du catalogue.
 */
export const GRADE_SETTINGS_FALLBACK: PagesWhenOff = { module: 'grades', pages: ['config.grades'] };

/** Le repli ouvre-t-il l'écran : module de la place habituelle coupé, et un de ses droits présent. */
export function opensWhenOff(
  fallback: PagesWhenOff | undefined,
  hasPage: (page: string) => boolean,
  isModuleOn: (module: PanelModuleKey) => boolean,
): boolean {
  return !!fallback && !isModuleOn(fallback.module) && fallback.pages.some((p) => hasPage(p));
}

/**
 * URL des modules, dans l'ordre de la navigation : le premier module actif (et ouvert au grade)
 * d'un espace est la cible de son onglet dans le bandeau. Les pages hors module (Dashboard, Grades &
 * accès, Déploiement, Paramètres généraux) n'y figurent pas et ne sont jamais fermées.
 */
export const MODULE_ROUTES: ModuleRoute[] = [
  { path: '/logs', module: 'logs' },
  { path: '/console', module: 'console' },
  { path: '/sanctions', module: 'sanctions' },
  { path: '/banwords', module: 'banwords' },
  { path: '/whitelist', module: 'whitelist' },
  { path: '/staff', module: 'staff' },
  { path: '/players', module: 'players' },
  { path: '/grades', module: 'grades' },
  // Configuration, dans l'ordre du menu : Contenu, Config, Validation.
  { path: '/config/blocks', module: 'blocks', pages: ['config.blocks'] },
  { path: '/config/items', module: 'items', pages: ['config.items'] },
  { path: '/config/entities', module: 'entities', pages: ['config.entities'] },
  { path: '/config/crafts', module: 'crafts', pages: ['config.crafts'] },
  { path: '/config/lootbox', module: 'lootbox', pages: ['config.lootbox'] },
  { path: '/config/jobs', module: 'jobs', pages: ['config.jobs'] },
  { path: '/config/recubejobs', module: 'recubejobs', pages: ['config.recubejobs'] },
  { path: '/config/weapons', module: 'weapons', pages: ['config.weapons'] },
  { path: '/config/minerais', module: 'minerais', pages: ['config.minerais'] },
  { path: '/config/worlds', module: 'worlds', pages: ['config.worlds'] },
  { path: '/config/cosmetics', module: 'cosmetics', pages: ['config.cosmetics'], pagesWhenOff: GRADE_SETTINGS_FALLBACK },
  { path: '/config/adminshop', module: 'adminshop', pages: ['config.adminshop'] },
  { path: '/config/factions', module: 'factions', pages: ['config.factions'] },
  { path: '/config/requests', module: 'requests', pages: ['config.requests'] },
  { path: '/config/discord', module: 'discord', adminOnly: true },
  { path: '/stats', module: 'stats' },
  // Section d'un module : la plus longue racine gagne, /stats/pays n'est donc pas « stats » seul.
  { path: '/stats/pays', module: 'statsFactions' },
  { path: '/stats/grades', module: 'statsGrades' },
  { path: '/stats/adminshop', module: 'statsAdminShop' },
  { path: '/stats/factions', module: 'statsGuilds' },
  { path: '/stats/box', module: 'statsBox' },
  { path: '/stats/boutique', module: 'statsShop' },
  { path: '/stats/personas', module: 'statsPersonas' },
];

/** Module auquel appartient une URL (racine la plus longue, sur une frontière de segment), ou null. */
export function moduleForPath(pathname: string): PanelModuleKey | null {
  let best: ModuleRoute | null = null;
  for (const r of MODULE_ROUTES) {
    if (pathname !== r.path && !pathname.startsWith(`${r.path}/`)) continue;
    if (!best || r.path.length > best.path.length) best = r;
  }
  return best?.module ?? null;
}

/**
 * URL des modules d'un espace, dans l'ordre de la navigation. Une route réservée aux admins n'est
 * comptée que pour eux : un onglet ne doit pas mener un modérateur vers un écran refusé.
 */
export function moduleRoutesOfSpace(space: PanelSpace, isAdmin: boolean): ModuleRoute[] {
  return MODULE_ROUTES.filter((r) => spaceFromPath(r.path) === space && (isAdmin || !r.adminOnly));
}

// ============================================================
// Capacités du satellite exigées par les pages de Supervision
// ============================================================
// Un projet à groupes (NationsGlory) sert chaque groupe par son propre satellite, qui annonce ce
// qu'il sait faire dans GET /api/_meta (`capabilities`, recopiées par groupe dans
// /api/panel/project-groups) : players, sanctions et stats dès qu'une base Mongo est déclarée,
// console avec Pterodactyl, ranks avec LuckPerms, online avec RabbitMQ, whitelist et banwords avec
// la game-api, logs avec Elasticsearch, profiles avec la ProfileAPI (satellite de groupe seulement,
// satellite v1.0.13 et plus), plus le nom de chaque module actif (watchdog, worlds...).
// Un groupe « grades seuls » (console et ranks, sans base) ne sert donc ni les joueurs ni les
// sanctions : ses pages sont masquées au lieu d'afficher des erreurs. Avec la ProfileAPI (profiles),
// la page Joueurs y reste ouverte en fiche allégée (cf playersSheetMode).
//
// Table à part, et non dérivée de la clé de module ou de la FeatureKey de la barre latérale :
//   - la page /logs est le journal du panel (LogsResource de l'API), sans lien avec la capacité
//     `logs` du satellite (Elasticsearch, journaux d'un serveur dans la page Server) ;
//   - il n'existe ni capacité `staff` ni `dashboard` ;
//   - /console/crashs (module watchdog du satellite) est sous le module « console » du panel.
//
// Correspondance, d'après les routes relayées (GatewayRoutes de l'API) et ce qui les sert côté
// satellite :
//   /console          console    /api/console/*, /api/servers/* (Pterodactyl)
//   /console/crashs   watchdog   /api/watchdog/reports (module watchdog, base « satellite »)
//   /sanctions        sanctions  /api/sanctions/* (base « moderation »)
//   /banwords         banwords   /api/config/banwords (game-api)
//   /whitelist        whitelist  /api/config/whitelist (game-api) ; à défaut whitelistServers, les
//                                seules listes des serveurs : /api/config/whitelist/servers (Pterodactyl)
//   /staff            stats      /api/staff/* (StaffPresenceService, base « stats »)
//   /players          players    /api/players/* (bases des joueurs) ; à défaut profiles, fiche
//                                allégée : /api/players/search et /{uuid}/basic (ProfileAPI)
//   /grades           ranks      /api/ranks/* (LuckPerms du groupe)
// Sans entrée, la page ne dépend d'aucune capacité : servie par le panel (Logs, Grades & accès),
// ou adaptée carte par carte (Dashboard, cf dashboardCards).
// ============================================================

interface SatelliteRoute {
  /** Racine d'URL : elle-même et tout ce qui est en dessous (racine la plus longue). */
  path: string;
  /** Capacité que le satellite du groupe actif doit annoncer. */
  capability: string;
  /**
   * Capacités qui ouvrent aussi la page, en version réduite, quand `capability` manque : la page
   * Joueurs en fiche allégée avec profiles seule (cf playersSheetMode).
   */
  reducedWith?: string[];
}

const SATELLITE_ROUTES: SatelliteRoute[] = [
  { path: '/console', capability: 'console' },
  { path: '/console/crashs', capability: 'watchdog' },
  { path: '/sanctions', capability: 'sanctions' },
  { path: '/banwords', capability: 'banwords' },
  // Groupe sans ModerationAPI (lobby NationsGlory) : la page ne montre que les listes des serveurs.
  { path: '/whitelist', capability: 'whitelist', reducedWith: ['whitelistServers'] },
  { path: '/staff', capability: 'stats' },
  { path: '/players', capability: 'players', reducedWith: ['profiles'] },
  { path: '/grades', capability: 'ranks' },
];

/** URL du Dashboard, page d'arrivée de la Supervision quand le satellite du groupe le sert. */
export const DASHBOARD_PATH = '/dashboard';

function trimPath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/** Entrée d'une page de Supervision (racine la plus longue), ou null si elle n'exige rien. */
function satelliteRouteForPath(pathname: string): SatelliteRoute | null {
  const path = trimPath(pathname);
  let best: SatelliteRoute | null = null;
  for (const r of SATELLITE_ROUTES) {
    if (path !== r.path && !path.startsWith(`${r.path}/`)) continue;
    if (!best || r.path.length > best.path.length) best = r;
  }
  return best;
}

/**
 * Capacité que la page exige et que le satellite du groupe actif n'annonce pas, ou null. Une
 * capacité de version réduite (reducedWith) suffit à ouvrir la page : la page Joueurs l'est avec
 * profiles seule, en fiche allégée.
 *
 * @param capabilities capacités du satellite du groupe actif ; null = inconnues (jeu sans groupes,
 *                     groupes pas encore lus, satellite jamais sondé) : rien n'est masqué
 */
export function missingCapability(pathname: string, capabilities: string[] | null): string | null {
  if (!capabilities) return null;
  const route = satelliteRouteForPath(pathname);
  if (!route) return null;
  const served = [route.capability, ...(route.reducedWith ?? [])].some((c) => capabilities.includes(c));
  return served ? null : route.capability;
}

/**
 * Fiche de la page Joueurs servie par le satellite du groupe actif :
 *   - full : fiche complète, lue dans les bases des joueurs (players), comme avant ; aussi avec des
 *     capacités inconnues (Paladium, groupes pas encore lus, satellite jamais sondé) ;
 *   - light : fiche allégée, profiles sans players (lobby sans base, ProfileAPI seule) : recherche
 *     par pseudo, identité et grades du groupe, aucune route lue dans Mongo (argent, homes, faction,
 *     métiers, market, sanctions, joueurs au hasard, autocomplétion).
 */
export type PlayersSheetMode = 'full' | 'light';

export function playersSheetMode(capabilities: string[] | null): PlayersSheetMode {
  return capabilities && !capabilities.includes('players') && capabilities.includes('profiles') ? 'light' : 'full';
}

/**
 * Cartes du Dashboard servies par le satellite du groupe actif. Capacités inconnues : toutes,
 * comme avant.
 *   - online : « Joueurs en ligne », « Staff en ligne » et les deux listes (/api/online/*) ;
 *   - sanctions : « Sanctions aujourd'hui » (/api/sanctions/today/count) ;
 *   - players : le clic sur un joueur en ligne ouvre sa fiche (/api/players/search), complète avec
 *     players, allégée avec profiles seule ;
 *   - light : Dashboard d'un groupe sans base de joueurs (lobby : capacités connues, sans players).
 *     Ni « Tickets ouverts » ni sanctions ; à la place, la recherche d'un joueur et ses grades, et :
 *   - servers : état des serveurs du groupe (Pterodactyl, console), Dashboard allégé seulement ;
 *   - ranks : grades LuckPerms du groupe (ranks), Dashboard allégé seulement ;
 *   - any : au moins une carte de données ; sans elle, « Tickets ouverts » (sans source) est masquée
 *     et le Dashboard le dit au lieu d'afficher des erreurs.
 * Nova, Iris (players) et Paladium (capacités inconnues) gardent le Dashboard d'avant.
 */
export interface DashboardCards {
  online: boolean;
  sanctions: boolean;
  players: boolean;
  light: boolean;
  servers: boolean;
  ranks: boolean;
  any: boolean;
}

export function dashboardCards(capabilities: string[] | null): DashboardCards {
  const has = (c: string) => !capabilities || capabilities.includes(c);
  const online = has('online');
  const sanctions = has('sanctions');
  const light = !!capabilities && !capabilities.includes('players');
  const servers = light && has('console');
  const ranks = light && has('ranks');
  return {
    online, sanctions, players: has('players') || has('profiles'), light, servers, ranks,
    any: online || sanctions || servers || ranks,
  };
}

/**
 * Le satellite du groupe actif sert-il la page ? Le Dashboard l'est dès qu'il a une carte ; une page
 * sans capacité exigée l'est toujours, comme tout avec des capacités inconnues.
 */
export function satelliteServes(pathname: string, capabilities: string[] | null): boolean {
  if (!capabilities) return true;
  if (trimPath(pathname) === DASHBOARD_PATH) return dashboardCards(capabilities).any;
  return missingCapability(pathname, capabilities) === null;
}
